import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import Select from "react-select";
import { FaTrashAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from 'sweetalert2';

export default function DoctorEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();

    const [specializations, setSpecializations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [doctorId, setDoctorId] = useState(null);
    const [userRole, setUserRole] = useState("doctor"); // Track user role

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        contact_number: "",
        address: "",
        bio: "",
        img: null,
        specialization_id: [],
        availability: [],
        role: "doctor"
    });

    const [errors, setErrors] = useState({});

    // Role options
    const roleOptions = [
        { value: "doctor", label: "Doctor" },
        { value: "sweeper", label: "Sweeper" }
    ];

    const days = [
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
        { value: "Sunday", label: "Sunday" }
    ];

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: "38px",
            borderRadius: "6px"
        })
    };

    const formatTime = (time) => {
        if (!time) return "";
        return time.slice(0, 5);
    };

    const showToast = (type, message) => {
        Swal.fire({
            icon: type,
            title: t(type),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    useEffect(() => {
        fetchDoctor();
    }, [id]);

    const fetchDoctor = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/doctors-edit/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            const data = res.data.doctor;
            setSpecializations(res.data.specializations || []);

            // Get user role from response
            const role = data.role || "doctor";
            setUserRole(role);

            if (data.doctor) {
                setDoctorId(data.id);

                const selectedSpecs = data.doctor.specializations?.map(s => s.specialization_id) || [];
                const availability = data.doctor.availabilities?.map(a => ({
                    day_of_week: a.day_of_week,
                    start_time: formatTime(a.start_time),
                    end_time: formatTime(a.end_time)
                })) || [];

                setForm({
                    name: data.name || "",
                    email: data.email || "",
                    password: "",
                    contact_number: data.doctor.contact_number || "",
                    address: data.doctor.address || "",
                    bio: data.doctor.bio || "",
                    img: null,
                    specialization_id: selectedSpecs,
                    availability: availability.length ? availability : [],
                    role: role
                });
            } else {
                // For sweeper or users without doctor record
                setForm({
                    name: data.name || "",
                    email: data.email || "",
                    password: "",
                    contact_number: "",
                    address: "",
                    bio: "",
                    img: null,
                    specialization_id: [],
                    availability: [],
                    role: role
                });
            }

        } catch (err) {
            console.error(err);
            showToast('error', t('Failed to load user details'));
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setForm({ ...form, [name]: files ? files[0] : value });
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRoleChange = (selectedOption) => {
        const role = selectedOption ? selectedOption.value : "doctor";
        setUserRole(role);

        // Reset form for sweeper - clear doctor-specific fields
        if (role === "sweeper") {
            setForm({
                ...form,
                role: role,
                contact_number: "",
                address: "",
                bio: "",
                specialization_id: [],
                availability: []
            });
        } else {
            setForm({
                ...form,
                role: role
            });
        }

        // Clear all errors when role changes
        setErrors({});
    };

    const handleAvailabilityChange = (index, field, value) => {
        const updated = [...form.availability];
        updated[index][field] = value;
        setForm({ ...form, availability: updated });
    };

    const addAvailabilityRow = () => {
        setForm({
            ...form,
            availability: [...form.availability, { day_of_week: "", start_time: "", end_time: "" }]
        });
        showToast('success', t('Availability row added'));
    };

    const removeAvailabilityRow = (index) => {
        if (form.availability.length === 1) {
            showToast('warning', t('You need at least one availability slot'));
            return;
        }
        const updated = form.availability.filter((_, i) => i !== index);
        setForm({ ...form, availability: updated });
        showToast('success', t('Availability row removed'));
    };

    const validateForm = () => {
        const newErrors = {};

        // Common validations for all roles
        if (!form.name.trim()) newErrors.name = [t('Name is required')];
        if (!form.email.trim()) newErrors.email = [t('Email is required')];
        if (form.password && form.password.length > 0 && form.password.length < 8) {
            newErrors.password = [t('Password must be at least 8 characters')];
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = [t('Please enter a valid email address')];
        }

        // Doctor-specific validations
        if (form.role === "doctor") {
            if (!form.contact_number.trim()) newErrors.contact_number = [t('Contact number is required')];
            if (!form.address.trim()) newErrors.address = [t('Address is required')];

            form.availability.forEach((avail, i) => {
                if (!avail.day_of_week) newErrors[`availability_${i}_day`] = [t('Day is required')];
                if (!avail.start_time) newErrors[`availability_${i}_start`] = [t('Start time is required')];
                if (!avail.end_time) newErrors[`availability_${i}_end`] = [t('End time is required')];
                if (avail.start_time && avail.end_time && avail.start_time >= avail.end_time) {
                    newErrors[`availability_${i}_time`] = [t('Start time must be before end time')];
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('error', t('Please fix the errors in the form'));
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const formData = new FormData();
            const updateId = doctorId || id;

            // Common fields for all roles
            formData.append("name", form.name);
            formData.append("email", form.email);
            if (form.password) formData.append("password", form.password);
            formData.append("role", form.role);
            formData.append("_method", "PUT");

            // Image (optional for all roles)
            if (form.img) formData.append("img", form.img);

            // Doctor-specific fields
            if (form.role === "doctor") {
                formData.append("contact_number", form.contact_number);
                formData.append("address", form.address);
                formData.append("bio", form.bio || "");

                form.specialization_id.forEach((specId, i) => {
                    formData.append(`specialization_id[${i}]`, specId);
                });

                form.availability.forEach((item, i) => {
                    if (item.day_of_week && item.start_time && item.end_time) {
                        formData.append(`availability[${i}][day_of_week]`, item.day_of_week);
                        formData.append(`availability[${i}][start_time]`, item.start_time);
                        formData.append(`availability[${i}][end_time]`, item.end_time);
                    }
                });
            }

            await axios.post(
                `${process.env.REACT_APP_API_URL}/doctors-update/${updateId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            const successMessage = form.role === "doctor"
                ? t('Doctor updated successfully!')
                : t('Sweeper updated successfully!');

            showToast('success', successMessage);
            setTimeout(() => navigate("/doctors"), 1500);

        } catch (err) {
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
                showToast('error', t('Please fix the validation errors'));
            } else if (err.response?.status === 500) {
                showToast('error', t('Server error. Please try again later.'));
            } else {
                showToast('error', t('Failed to update user'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Check if current role is doctor
    const isDoctor = form.role === "doctor";

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="p-4 shadow-sm" style={{ borderRadius: "12px" }}>
                        <h4 className="mb-4">{isDoctor ? t("Edit Doctor") : t("Edit Sweeper")}</h4>

                        <Form onSubmit={handleSubmit}>

                            {/* Role Selection */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={roleOptions}
                                        value={roleOptions.find(opt => opt.value === form.role)}
                                        onChange={handleRoleChange}
                                        placeholder={t('Select role')}
                                    />
                                    {errors.role && (
                                        <small className="text-danger">{errors.role?.[0]}</small>
                                    )}
                                </Col>
                            </Row>

                            {/* Name + Email + Password */}
                            <Row className="mb-3">
                                <Col md={3}>
                                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                        placeholder={t('Enter name')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={3}>
                                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                        placeholder="Email"
                                        type="email"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Update Image</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        onChange={handleChange}
                                        accept="image/*"
                                    />
                                    <small className="text-muted">{t('Upload a new profile picture (optional)')}</small>
                                </Col>

                            </Row>

                            {/* Profile Image - Shows for ALL roles */}
                            <Row className="mb-3">

                            </Row>

                            {/* Doctor-specific fields - ONLY shown when role is doctor */}
                            {isDoctor && (
                                <>
                                    {/* Contact + Address */}
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Form.Label>Password</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    name="password"
                                                    value={form.password}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.password}
                                                    placeholder={t('Enter password')}
                                                    type={showPassword ? "text" : "password"}
                                                />
                                                <span className="d-block mt-1">{t('Leave blank to keep current password')}</span>
                                                <button
                                                    type="button"
                                                    className="btn position-absolute end-0 top-0"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        padding: '0.375rem 0.75rem',
                                                        color: '#6c757d',
                                                        top: '50%',
                                                        transform: 'translateY(10%)',
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.password?.[0]}
                                                </Form.Control.Feedback>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label>Contact Number <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                name="contact_number"
                                                value={form.contact_number}
                                                onChange={handleChange}
                                                isInvalid={!!errors.contact_number}
                                                placeholder={t('Enter contact number')}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.contact_number?.[0]}
                                            </Form.Control.Feedback>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                name="address"
                                                value={form.address}
                                                onChange={handleChange}
                                                isInvalid={!!errors.address}
                                                placeholder={t('Enter address')}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.address?.[0]}
                                            </Form.Control.Feedback>
                                        </Col>
                                    </Row>

                                    {/* Bio */}
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Bio</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="bio"
                                                value={form.bio}
                                                onChange={handleChange}
                                                placeholder={t('Enter doctor bio')}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Specializations */}
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Specializations</Form.Label>
                                            <Select
                                                isMulti
                                                styles={customSelectStyles}
                                                options={specializations.map(sp => ({
                                                    value: sp.id,
                                                    label: sp.name
                                                }))}
                                                value={specializations
                                                    .filter(sp => form.specialization_id.includes(sp.id))
                                                    .map(sp => ({ value: sp.id, label: sp.name }))
                                                }
                                                onChange={(selected) =>
                                                    setForm({
                                                        ...form,
                                                        specialization_id: selected ? selected.map(i => i.value) : []
                                                    })
                                                }
                                                placeholder={t('Select specializations')}
                                            />
                                            <small className="text-muted">{t('You can select multiple specializations')}</small>
                                        </Col>
                                    </Row>

                                    {/* Availability */}
                                    <h5 className="mt-3 mb-3">{t('Availability')} <span className="text-danger">*</span></h5>

                                    {form.availability.map((row, index) => (
                                        <div key={index}>
                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Label>Day</Form.Label>
                                                    <Select
                                                        styles={customSelectStyles}
                                                        options={days}
                                                        value={days.find(d => d.value === row.day_of_week)}
                                                        onChange={(selected) =>
                                                            handleAvailabilityChange(index, "day_of_week", selected.value)
                                                        }
                                                        placeholder={t('Select day')}
                                                    />
                                                    {errors[`availability_${index}_day`] && (
                                                        <small className="text-danger">{errors[`availability_${index}_day`]?.[0]}</small>
                                                    )}
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label>Start Time</Form.Label>
                                                    <Form.Control
                                                        type="time"
                                                        value={row.start_time}
                                                        onChange={(e) =>
                                                            handleAvailabilityChange(index, "start_time", e.target.value)
                                                        }
                                                        isInvalid={!!errors[`availability_${index}_start`]}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[`availability_${index}_start`]?.[0]}
                                                    </Form.Control.Feedback>
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label>End Time</Form.Label>
                                                    <Form.Control
                                                        type="time"
                                                        value={row.end_time}
                                                        onChange={(e) =>
                                                            handleAvailabilityChange(index, "end_time", e.target.value)
                                                        }
                                                        isInvalid={!!errors[`availability_${index}_end`]}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[`availability_${index}_end`]?.[0]}
                                                    </Form.Control.Feedback>
                                                </Col>

                                                <Col md={2} className="d-flex align-items-end">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger w-100 d-flex justify-content-center align-items-center gap-2"
                                                        onClick={() => removeAvailabilityRow(index)}
                                                    >
                                                        <FaTrashAlt /> {t('Remove')}
                                                    </ButtonComponent>
                                                </Col>
                                            </Row>
                                            {errors[`availability_${index}_time`] && (
                                                <div className="mb-3">
                                                    <small className="text-danger">{errors[`availability_${index}_time`]?.[0]}</small>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-secondary mb-3"
                                        onClick={addAvailabilityRow}
                                        text={t('Add Availability')}
                                    />
                                </>
                            )}

                            {/* Submit Button */}
                            <div className="text-end">
                                <ButtonComponent
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={loading}
                                    text={loading ? t("Updating...") : (isDoctor ? t("Update Doctor") : t("Update Sweeper"))}
                                />
                            </div>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}